import { Button, CircularProgress } from "@mui/material";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import cartIcon from "../../assets/image/png/cartIcon.png";
import vanIcon from "../../assets/image/svg/van.svg";
import { useEffect, useState } from "react";
import {
  CartProduct,
  ProductMap,
} from "../../features/createSecondaryOrder/models";
import "./CartSummaryFooter.css";
import { customStyle } from "../../features/viewOrder/ViewOrderUtils";
import { ReactComponent as PdfIcon } from "../../assets/image/svg/pdfIcon.svg";
import { getUomLabel, showUom, truncateToTwoDecimals } from "./DmsUtils";
import useShortcutKeys from "./ShortcutKeys";

const defaultTotalInfo = {
  numberOfProducts: 0,
  totalCaseQuantity: 0,
  totalPieceQuantity: 0,
  totalOtherUnitQuantity: 0,
  totalAmount: 0,
};

interface CartSummaryFooterProps {
  cartProducts: ProductMap | null;
  onButtonClick: (value?: string) => void;
  onDeleteButtonClick?: (value?: string) => void;
  buttonText: string;
  endIcon?: boolean;
  loadout?: boolean;
  reviewLoadout?: boolean;
  disabled?: boolean;
  totalAmountAndQtyInfo?: any;
  loading?: boolean;
  rsu?: boolean;
  isPrimary?: boolean;
  displaySaveButton?: boolean;
  displayDeleteButton?: boolean;
  hideShortcutKeys?: boolean;
}

export const calculateTotalAmountAndQuantities = (products: CartProduct[]) => {
  let totalAmount = 0;
  let totalCaseQuantity = 0;
  let totalPieceQuantity = 0;
  let totalOtherUnitQuantity = 0;

  products?.forEach((product) => {
    const {
      pieceQuantity,
      caseQuantity,
      otherUnitQuantity,
      price,
      casePrice,
      otherUnitPrice,
    } = product;
    totalAmount +=
      pieceQuantity * price +
      caseQuantity * casePrice +
      otherUnitQuantity * otherUnitPrice;
    totalCaseQuantity += caseQuantity;
    totalPieceQuantity += pieceQuantity;
    totalOtherUnitQuantity += otherUnitQuantity;
  });

  return {
    numberOfProducts: products?.length,
    totalCaseQuantity,
    totalPieceQuantity,
    totalOtherUnitQuantity,
    totalAmount,
  };
};

const CartSummaryFooter: React.FC<CartSummaryFooterProps> = ({
  cartProducts,
  onButtonClick = () => {},
  onDeleteButtonClick = () => {},
  buttonText = "Submit",
  endIcon = false,
  loadout = false,
  reviewLoadout = false,
  disabled = false,
  totalAmountAndQtyInfo = {},
  loading = false,
  rsu = false,
  isPrimary = false,
  displaySaveButton = false,
  displayDeleteButton = false,
  hideShortcutKeys = false,
}) => {
  const [totalInfo, setTotalInfo] =
    useState<Record<string, number>>(defaultTotalInfo);

  const ConfigDictionary = JSON.parse(localStorage.getItem("ConfigDictionary") ?? "{}");
  const { caseUnitLabel, otherUnitLabel, packUnitLabel, currency, designationBasedUomSecondary } = ConfigDictionary;
  const uomLabels = getUomLabel();
  const { caseUnitLabelPrimary, otherUnitLabelPrimary, packUnitLabelPrimary, createPrimaryOrderUom } = ConfigDictionary;
 const { positiveCta } = useShortcutKeys();

  useEffect(() => {
    if (Object.keys(totalAmountAndQtyInfo).length > 0) {
      setTotalInfo(totalAmountAndQtyInfo);
    } else {
      const totalData = calculateTotalAmountAndQuantities(
        Object.values(cartProducts ?? {})
      );
      setTotalInfo(totalData);
    }
  }, [cartProducts]);

  const formattedAmount = new Intl.NumberFormat("en-IN").format(
    Number(truncateToTwoDecimals((totalInfo?.netAmount ? totalInfo?.netAmount : totalInfo?.totalAmount) ?? 0))
  );

  if (isPrimary) {
    return (
      <div className="cart-summary cart-summary-primary">
        <div className="cart-info">
          <div className="checkout-cart-icon">
            <img alt="cart-icon" src={cartIcon} height="30px" width="30px" />
            <span className="checkout-cart-count">
              {totalInfo.numberOfProducts}
            </span>
          </div>
          <div className="cart-details">
            <div>
              Total Quantity:{" "}
              {caseUnitLabelPrimary && showUom("caseQty", createPrimaryOrderUom) && (
                <strong>
                  {totalInfo.totalCaseQuantity} {caseUnitLabelPrimary}{" "}
                </strong>
              )}
              {otherUnitLabelPrimary && showUom("otherQty", createPrimaryOrderUom) && (
                <strong>
                  {totalInfo.totalOtherUnitQuantity} {otherUnitLabelPrimary}{" "}
                </strong>
              )}
              {packUnitLabelPrimary && showUom("pieceQty", createPrimaryOrderUom) && (
                <strong>
                  {totalInfo.totalPieceQuantity} {packUnitLabelPrimary}
                </strong>
              )}
            </div>
            <div>
              Total Amount:{" "}
              <strong>
                {currency} {formattedAmount}
              </strong>
            </div>
          </div>
        </div>

        <div>
          <Button
            variant="contained"
            endIcon={
              endIcon &&
              !loading && (
                <ArrowForwardIosIcon
                  sx={{
                    height: "15px",
                    width: "15px",
                  }}
                />
              )
            }
            sx={{
              backgroundColor: "var(--dashboard-theme-color)",
              ":hover": {
                background: "var(--dashboard-theme-color)",
                boxShadow: "none",
              },
              textTransform: "none",
              boxShadow: "none",
              borderRadius: "8px",
              "&.Mui-disabled": {
                color: "#7C7C7C",
                fontWeight: 600,
              },
              minWidth: "130px",
              minHeight: "37px",
            }}
            onClick={loading ? () => {} : () => onButtonClick()}
            size={"medium"}
            disabled={disabled}
          >
            {loading ? <CircularProgress style={customStyle} /> : buttonText}
            {!hideShortcutKeys && !loading && <span style={{fontSize:"9.5px"}}>&nbsp;{positiveCta}</span>}
          </Button>
        </div>
      </div>
    );
  }

  const designationEnabled  =  designationBasedUomSecondary?.enable ?? false;
  const availableDesignations = designationBasedUomSecondary?.roles?.designationKeys?.map(((item: { type: any; }) => item?.type)) || [];
  const currentDesignation = JSON.parse(localStorage.authContext).user.designation || [];

  const designation = availableDesignations.find((type: any) => 
    currentDesignation.includes(type)
  );
  
  const designationKeys = designationBasedUomSecondary?.roles?.designationKeys ?? [];
  const matchedRole = designationKeys.find((role: { type: string; uomAllowed: any }) => role.type === designation);

  if(designationEnabled && designation && designationKeys && matchedRole && !loadout && !reviewLoadout) {
    return (
      <div
        style={{
          height: rsu ? "25%" : loadout ? "100%" : "",
          boxShadow: !reviewLoadout ? "none" : "",
        }}
        className="cart-summary cart-summary-primary"
      >
        <div className="cart-info">
          <div className="checkout-cart-icon">
            <img
              alt="cart-icon"
              src={loadout ? vanIcon : cartIcon}
              height={loadout ? "35px" : "30px"}
              width={loadout ? "35px" : "30px"}
            />
            <span
              className="checkout-cart-count"
              style={{
                top: loadout ? "-8px" : "",
                right: loadout ? "-12px" : "",
              }}
            >
              {totalInfo.numberOfProducts}
            </span>
          </div>
          {(totalInfo.numberOfProducts > 0 || !loadout) && (
            <div
              style={{
                fontSize: loadout ? "16.8px" : "",
                letterSpacing: loadout ? "-0.44px" : "",
              }}
              className="cart-details"
            >
              <div>
                Total Quantity:{" "}
                {matchedRole.uomAllowed.case?.trim() && (<strong>
                  {totalInfo.totalCaseQuantity} {caseUnitLabel}
                </strong>)}
                
                {matchedRole.uomAllowed.piece?.trim() && (<strong>
                  {totalInfo.totalPieceQuantity} {packUnitLabel}
                </strong>)}
              </div>
              <div>
                Total Amount:{" "}
                <strong>
                  {currency} {formattedAmount}
                </strong>
              </div>
            </div>
          )}
        </div>
  
        <div>
          {loadout && reviewLoadout && (
            <Button
              variant="outlined"
              size="large"
              endIcon={
                endIcon && (
                  <ArrowForwardIosIcon sx={{ height: "15px", width: "15px" }} />
                )
              }
              sx={{
                ":hover": {
                  boxShadow: "none",
                  border: "1px solid #02C6B1",
                },
                textTransform: "none",
                boxShadow: "none",
                border: "1px solid #02C6B1",
                color: "#02C6B1",
                mr: "10px",
                borderRadius: "8px",
                fontWeight: 600,
              }}
              onClick={loading ? () => {} : () => onButtonClick("IN-DRAFT")}
            >
              Save Progress
            </Button>
          )}
          {buttonText.length > 0 && (
            <Button
              variant="contained"
              endIcon={
                endIcon &&
                !loading && (
                  <ArrowForwardIosIcon
                    sx={{
                      height: loadout && !reviewLoadout ? "13px" : "15px",
                      width: "15px",
                      marginLeft:
                        loadout && !reviewLoadout ? "-4px !important" : "",
                    }}
                  />
                )
              }
              sx={{
                backgroundColor: "var(--dashboard-theme-color)",
                ":hover": {
                  background: "var(--dashboard-theme-color)",
                  boxShadow: "none",
                },
                textTransform: "none",
                boxShadow: "none",
                borderRadius: "8px",
                "&.Mui-disabled": {
                  color: "#7C7C7C",
                  fontWeight: 600,
                },
                minWidth: "130px",
                minHeight: "37px",
              }}
              onClick={loading ? () => {} : () => onButtonClick()}
              size={loadout && reviewLoadout ? "large" : rsu ? "small" : "medium"}
              disabled={disabled}
            >
              {loading ? <CircularProgress style={customStyle} /> : buttonText}
              {!hideShortcutKeys &&  !loading && <span style={{fontSize:"9.5px"}}>&nbsp;{positiveCta}</span>}
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        height: rsu ? "25%" : loadout ? "100%" : "",
        boxShadow: !reviewLoadout ? "none" : "",
        alignItems: loadout ? "flex-end" : "",
        padding: loadout && reviewLoadout ? "8px 10px" : "",
      }}
      className="cart-summary cart-summary-primary"
    >
      <div
        className="cart-info"
        style={{ alignItems: loadout ? "flex-start" : "" }}
      >
        <div className="checkout-cart-icon">
          <img
            alt="cart-icon"
            src={loadout ? vanIcon : cartIcon}
            height={loadout ? "32px" : "30px"}
            width={loadout ? "70%" : "30px"}
          />
          <span
            className="checkout-cart-count"
            style={{
              top: loadout ? "-6px" : "",
              right: loadout ? "2px" : "",
            }}
          >
            {totalInfo.numberOfProducts}
          </span>
        </div>
        {(totalInfo.numberOfProducts > 0 || !loadout) && (
          <div
            style={{
              fontSize: loadout ? "16.8px" : "",
              letterSpacing: loadout ? "-0.44px" : "",
              marginLeft: loadout ? "30px" : "",
            }}
            className="cart-details"
          >
            <div>
              Total Quantity:{" "}
              {uomLabels?.caseUnitLabel && <strong>
                {totalInfo.totalCaseQuantity} {uomLabels?.caseUnitLabel}
              </strong>}
              {uomLabels?.otherUnitLabel && (
                <strong>
                  , {totalInfo.totalOtherUnitQuantity} {uomLabels?.otherUnitLabel}
                </strong>
              )}
              {uomLabels?.packUnitLabel && <strong>
                , {totalInfo.totalPieceQuantity} {uomLabels?.packUnitLabel}
              </strong>}
            </div>
            <div>
              Total Amount:{" "}
              <strong>
                {currency} {formattedAmount}
              </strong>
            </div>
          </div>
        )}
      </div>

      <div>
        {loadout && (reviewLoadout || displayDeleteButton) && (
          <Button
            variant="outlined"
            size={displayDeleteButton ? "medium" : "large"}
            endIcon={
              endIcon &&
              !displayDeleteButton && (
                <ArrowForwardIosIcon sx={{ height: "15px", width: "15px" }} />
              )
            }
            sx={{
              ":hover": {
                boxShadow: "none",
                border: displayDeleteButton
                  ? "1px solid #c64c01"
                  : "1px solid #02C6B1",
              },
              textTransform: "none",
              boxShadow: "none",
              border: displayDeleteButton
                ? "1px solid #c64c01"
                : "1px solid #02C6B1",
              color: displayDeleteButton ? "#c64c01" : "#02C6B1",
              mr: displayDeleteButton ? "15px" : "10px",
              borderRadius: "8px",
              fontWeight: 600,
            }}
            onClick={() => {
              if (!loading) {
                if (displayDeleteButton) {
                  onDeleteButtonClick();
                } else {
                  onButtonClick("IN-DRAFT");
                }
              }
            }}
          >
            {!displaySaveButton && !displayDeleteButton && <PdfIcon />}
            {!displaySaveButton && "\u00A0\u00A0"}
            {displayDeleteButton
              ? "Delete Loadout"
              : displaySaveButton
              ? "Save Progress"
              : "Print Loadslip"}
          </Button>
        )}
        {buttonText.length > 0 && (
          <Button
            variant="contained"
            endIcon={
              endIcon &&
              !loading && (
                <ArrowForwardIosIcon
                  sx={{
                    height: loadout && !reviewLoadout ? "13px" : "15px",
                    width: "15px",
                    marginLeft:
                      loadout && !reviewLoadout ? "-4px !important" : "",
                  }}
                />
              )
            }
            sx={{
              backgroundColor: "var(--dashboard-theme-color)",
              ":hover": {
                background: "var(--dashboard-theme-color)",
                boxShadow: "none",
              },
              textTransform: "none",
              boxShadow: "none",
              borderRadius: "8px",
              "&.Mui-disabled": {
                color: "#7C7C7C",
                fontWeight: 600,
              },
              minWidth: "130px",
              minHeight: "37px",
            }}
            onClick={loading ? () => {} : () => onButtonClick()}
            size={loadout && reviewLoadout ? "large" : rsu ? "small" : "medium"}
            disabled={disabled}
          >
            {loading ? <CircularProgress style={customStyle} /> : buttonText}
            {!hideShortcutKeys &&  !loading && <span style={{fontSize:"9.5px"}}>&nbsp;{positiveCta}</span>}
          </Button>
        )}
      </div>
    </div>
  );
};

export default CartSummaryFooter;
